import QueryString from 'qs'
import { apiClient } from '../client'
import { handleFilesFormData, priorityCompareFn } from '../../utils/general'
import { updateFile } from '../globalFunctions'
import { UpdateFileParams, UpdateFileResponse } from '../globalTypes'
import { getQueryData } from '../helpers'
import { handleUpdatePayload } from './helpers'
import { DeleteImageParams, Image, UpdateImageParams, UploadImageParams, UploadImageResponse, GetImagesResponse } from './types'

export const getImagesById = async (ids: string | number | number[], module: string, collection: string) => {
    const paramsLink = `noLimit=1&order[priority]=asc&criteria[module]=${module}&criteria[collection]=${collection}`
    const paramsEncoded = QueryString.stringify(QueryString.parse(paramsLink))

    if (Array.isArray(ids)) {
        const promises = ids.map(async (id) => await apiClient(`v1/file/parent/${id}?${paramsEncoded}`))
        const results = (await Promise.all(promises)) as GetImagesResponse[]

        const allImages = results.flatMap((res) => res.data.items || [])
        return allImages.sort(priorityCompareFn)
    }

    const response = (await apiClient(`v1/file/parent/${ids}?${paramsEncoded}`)) as GetImagesResponse
    return response.data.items.sort(priorityCompareFn)
}

// on upload image priority is handled on backend
export const uploadImages = async (params: UploadImageParams) => {
    const { files, entityData, url, module, collection } = params
    const formData = handleFilesFormData(files, entityData, module, collection)

    const response = await apiClient(`v1/file/upload/${url}`, {
        method: 'POST',
        data: formData,
    })

    return response.data as UploadImageResponse
}

const deleteImageById = async (id: string) => {
    const response = await apiClient(`v1/file/${id}`, {
        method: 'DELETE',
    })

    return response.data as UpdateFileResponse
}

export const deleteImages = async (params: DeleteImageParams) => {
    const { ids, collection, module, parent } = params
    const images = getQueryData([collection, module, parent]) as Image[]

    const imagesToDelete = ids?.map((id) => +id)
    const imagesToUpdate: UpdateImageParams[] = images
        .filter((image) => !imagesToDelete.includes(image.id))
        .map((image, index) => {
            const newPriority = index + 1

            return {
                id: image.id,
                collection: image.collection,
                module: image.module,
                parent: image.parent.toString(),
                priority: newPriority,
                isPriorityUpdate: image.priority !== newPriority,
            }
        })

    const response = await Promise.all(ids.map((id) => deleteImageById(id)))
    await updateImages(imagesToUpdate)

    return response
}

export const updateImages = async (images: UpdateImageParams[]) => {
    const payloads = images.map((image) => handleUpdatePayload(image)).filter((payload): payload is UpdateFileParams => payload !== null)
    const response = await Promise.all(payloads.map((payload) => updateFile(payload).catch((error) => ({ error }))))

    return response as UpdateFileResponse[]
}

export const syncImage = async (id: number) => {
    const response = await apiClient(`v1/file/sync/${id}`, {
        method: 'PUT',
    })

    return response
}

export const getImageByUrl = async (id: number, size?: 'original' | '', timestamp?: number | null) => {
    const sizeParam = size === 'original' ? '/original' : ''
    const timestampParam = timestamp ? `?${timestamp}` : ''

    const response = await apiClient(`v1/file/show/${id}${sizeParam}${timestampParam}`, {
        method: 'GET',
        responseType: 'blob',
    })

    return {
        id: id,
        blob: response.data,
    }
}
